import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import PageHelpLink from "../../Components/Common/PageHelpLink";
import { helpArticleUrl, helpUrlForRoute } from "../../constants/helpRouteMap";
import {
  bulkRestoreContacts,
  clearHardBounceFlag,
  Contact,
  ContactExportFilters,
  ContactFieldDefinition,
  createContact,
  createSegment,
  createTag,
  deleteContact,
  downloadContactsExportCsv,
  ExportSessionProgress,
  getContactFieldDefinitions,
  getContacts,
  getExportSessions,
  getInactiveLastBulkDelete,
  getTags,
  InactiveLastBulkDelete,
  loadTagOptions,
  reactivateContact,
  releaseQuarantineContacts,
  resendDoiContact,
  resolveTagOptionsByIds,
  restoreContact,
  updateContact,
} from "../../helpers/audience_helper";
import {
  buildFieldsQueryParam,
  defaultOpForFieldType,
  FieldFilterEntry,
  formatFieldFilterChip,
  isFieldFilterActive,
  operatorLabel,
  operatorsForFieldType,
  parseInValues,
} from "../../helpers/fieldFilters";
import { buildSegmentRulesFromContactFilters } from "../../helpers/segmentRules";
import ContactTypedFieldInputs, {
  buildFieldsPayload,
  CustomFieldValueMap,
  valuesFromContactFields,
} from "./ContactTypedFieldInputs";
import ContactExportModal from "./ContactExportModal";
import {
  digitsOnly,
  formatPhoneInput,
  formatPhoneListCell,
} from "../../helpers/phoneDisplay";
import {
  apiErrorMessage as integrationApiError,
  getInstalledIntegrations,
  syncShopifyContacts,
  syncUappiContacts,
  syncVtexContacts,
} from "../../helpers/integrations_helper";

type TagOption = { value: number; label: string };
type TagMatch = "and" | "or";

const PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 350;
/** Colunas extras na tabela: só campos filterable (evita tabela infinita). */
const MAX_FILTERABLE_COLUMNS = 3;

/**
 * Lista + criar/editar contatos (API /api/v1/contacts).
 * Filtro multi-tag + telefone + fields tipados filterable (DOC 06 · LAC-079 / LAC-108).
 * UX: debounce, paginação, criar tag no modal, confirmar exclusão (DOC 13).
 * F2: “Salvar filtros como segmento” quando status/tags/campos estão ativos.
 */
const ContactsList = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  document.title = t("contacts.documentTitle");

  const statusFromUrl = searchParams.get("status") || "";
  const trashedFromUrl =
    searchParams.get("trashed") === "only" ? ("only" as const) : "";
  const searchFromUrl = searchParams.get("search") || "";
  const phoneFromUrl = searchParams.get("phone") || "";
  const hardBounceFromUrl =
    searchParams.get("hard_bounce_flagged") === "1" ||
    searchParams.get("hard_bounce_flagged") === "true";

  const [contacts, setContacts] = useState<Contact[]>([]);
  /** null = ainda não consultou; evita preload fetchAllTags (LAC-103). */
  const [tagsExist, setTagsExist] = useState<boolean | null>(null);
  const [fieldDefs, setFieldDefs] = useState<ContactFieldDefinition[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [search, setSearch] = useState(searchFromUrl);
  const [appliedSearch, setAppliedSearch] = useState(searchFromUrl);
  // Filtro telefone: debounce como search; API aceita máscara (LAC-108).
  const [phone, setPhone] = useState(phoneFromUrl);
  const [appliedPhone, setAppliedPhone] = useState(phoneFromUrl);
  const [tagFilters, setTagFilters] = useState<TagOption[]>([]);
  const [tagMatch, setTagMatch] = useState<TagMatch>("or");
  const [statusFilter, setStatusFilter] = useState(statusFromUrl);
  const [trashedFilter, setTrashedFilter] = useState<"" | "only">(trashedFromUrl);
  /** Só e-mails inválidos (tag auto hard bounce) — deep-link ?hard_bounce_flagged=1. */
  const [hardBounceOnly, setHardBounceOnly] = useState(hardBounceFromUrl);
  const [fieldFilters, setFieldFilters] = useState<
    Record<string, FieldFilterEntry>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [bulkRestoring, setBulkRestoring] = useState(false);
  const [selectedTrashed, setSelectedTrashed] = useState<Set<number>>(new Set());
  const [resendingDoiId, setResendingDoiId] = useState<number | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  // DDI default 55 (hard rule BR) — editável; número só dígitos na API.
  const [countryCode, setCountryCode] = useState("55");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [customFields, setCustomFields] = useState<CustomFieldValueMap>({});
  const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [newTagName, setNewTagName] = useState("");
  const [creatingTag, setCreatingTag] = useState(false);
  const [newTagError, setNewTagError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<Contact | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [reactivating, setReactivating] = useState<Contact | null>(null);
  const [reactivatingBusy, setReactivatingBusy] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState<ContactExportFilters>({});
  const [exportResumeId, setExportResumeId] = useState<number | null>(null);
  const [exportRecent, setExportRecent] = useState<ExportSessionProgress[]>([]);
  const [exportHistoryBusyId, setExportHistoryBusyId] = useState<number | null>(
    null
  );
  /** Snapshot da última higiene — banner na lixeira (LAC-129 U5). */
  const [lastHygieneBulk, setLastHygieneBulk] =
    useState<InactiveLastBulkDelete | null>(null);
  const [restoringHygieneBulk, setRestoringHygieneBulk] = useState(false);

  const sortedFieldDefs = useMemo(
    () =>
      [...fieldDefs].sort(
        (a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label)
      ),
    [fieldDefs]
  );

  const filterableDefs = useMemo(
    () => sortedFieldDefs.filter((d) => d.is_filterable),
    [sortedFieldDefs]
  );

  const tableFieldDefs = useMemo(
    () => filterableDefs.slice(0, MAX_FILTERABLE_COLUMNS),
    [filterableDefs]
  );

  const activeFieldFilterEntries = useMemo(
    () =>
      Object.entries(fieldFilters).filter(([, v]) => isFieldFilterActive(v)),
    [fieldFilters]
  );

  const fieldsQueryParam = useMemo(
    () => buildFieldsQueryParam(fieldFilters),
    [fieldFilters]
  );

  const hasActiveFilters = Boolean(
    appliedSearch ||
      appliedPhone ||
      tagFilters.length > 0 ||
      statusFilter ||
      trashedFilter === "only" ||
      hardBounceOnly ||
      activeFieldFilterEntries.length > 0
  );
  // Segmentos v1 só cobrem status/tags/campos — search/phone/lixeira ficam de fora.
  const canSaveAsSegment = Boolean(
    statusFilter ||
      tagFilters.length > 0 ||
      activeFieldFilterEntries.length > 0
  );
  const hasNoTags = tagsExist === false;
  const hasNoCustomFields = fieldDefs.length === 0;

  const [saveSegmentOpen, setSaveSegmentOpen] = useState(false);
  const [saveSegmentName, setSaveSegmentName] = useState("");
  const [saveSegmentBusy, setSaveSegmentBusy] = useState(false);
  const [saveSegmentError, setSaveSegmentError] = useState<string | null>(null);
  // Empty state: CTA sync se qualquer loja ecommerce.sync estiver conectada.
  const [storeSlug, setStoreSlug] = useState<string | null>(null);
  const [syncingStore, setSyncingStore] = useState(false);

  const probeTagsExist = useCallback(async () => {
    try {
      const res = await getTags({ per_page: 1, page: 1 });
      setTagsExist((res?.meta?.total ?? 0) > 0);
    } catch {
      setTagsExist(null);
    }
  }, []);

  // Deep-link ?tag_ids=1,2 (ex.: Hub VTEX → Contatos) sem fetchAllTags.
  useEffect(() => {
    const raw = searchParams.get("tag_ids");
    if (!raw) {
      return;
    }
    const ids = raw
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length === 0) {
      return;
    }
    let cancelled = false;
    void resolveTagOptionsByIds(ids).then((opts) => {
      if (!cancelled && opts.length > 0) {
        setTagFilters(opts);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const loadFieldDefs = useCallback(async () => {
    try {
      const res = await getContactFieldDefinitions({ per_page: 30 });
      if (res?.success) {
        setFieldDefs(res.data || []);
      }
    } catch {
      // Sem catálogo: form nativo continua; filtros tipados somem.
    }
  }, []);

  const loadExportRecent = useCallback(async () => {
    try {
      const res = await getExportSessions(8);
      if (res?.success) {
        setExportRecent(res.data || []);
      }
    } catch {
      // Histórico é opcional — lista de contatos segue.
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getContacts({
        search: appliedSearch || undefined,
        phone: appliedPhone || undefined,
        status: statusFilter || undefined,
        trashed: trashedFilter === "only" ? "only" : undefined,
        tag_ids: tagFilters.length
          ? tagFilters.map((opt) => opt.value)
          : undefined,
        tag_match: tagFilters.length > 1 ? tagMatch : undefined,
        fields: fieldsQueryParam,
        hard_bounce_flagged: hardBounceOnly ? 1 : undefined,
        page,
        per_page: PER_PAGE,
      });
      if (!res?.success) {
        setError(t("contacts.error.load"));
        setContacts([]);
        return;
      }
      setContacts(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
      if (trashedFilter === "only") {
        setSelectedTrashed(new Set());
      }
    } catch (e: any) {
      setError(e?.message || t("contacts.error.load"));
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [
    appliedSearch,
    appliedPhone,
    tagFilters,
    tagMatch,
    statusFilter,
    trashedFilter,
    hardBounceOnly,
    fieldsQueryParam,
    page,
    t,
  ]);

  useEffect(() => {
    probeTagsExist();
    loadFieldDefs();
    loadExportRecent();
  }, [probeTagsExist, loadFieldDefs, loadExportRecent]);

  // Busca com debounce — evita request a cada tecla (DOC 13 § 3.8 espírito).
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      setAppliedSearch((prev) => {
        if (prev === next) {
          return prev;
        }
        setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = phone.trim();
      setAppliedPhone((prev) => {
        if (prev === next) {
          return prev;
        }
        setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [phone]);

  useEffect(() => {
    load();
  }, [load]);

  // Lixeira: mostra undo da última higiene se ainda houver snapshot no cache.
  useEffect(() => {
    if (trashedFilter !== "only") {
      setLastHygieneBulk(null);
      return;
    }
    let cancelled = false;
    getInactiveLastBulkDelete()
      .then((res) => {
        if (cancelled) {
          return;
        }
        if (res?.success && res.data && res.data.count > 0) {
          setLastHygieneBulk(res.data);
        } else {
          setLastHygieneBulk(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLastHygieneBulk(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [trashedFilter]);

  // Só consulta Hub VTEX no empty state (evita call em lista cheia).
  useEffect(() => {
    if (loading || contacts.length > 0 || hasActiveFilters) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await getInstalledIntegrations();
        if (cancelled || !res?.success) {
          return;
        }
        const row = (res.data || []).find(
          (i) => i.enabled && i.capability === "ecommerce.sync"
        );
        if (!cancelled) {
          setStoreSlug(row?.provider_slug ?? null);
        }
      } catch {
        if (!cancelled) setStoreSlug(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, contacts.length, hasActiveFilters]);

  const onSyncStoreFromEmpty = async () => {
    if (!storeSlug) return;
    setSyncingStore(true);
    try {
      if (storeSlug === "shopify") {
        await syncShopifyContacts();
      } else if (storeSlug === "uappi") {
        await syncUappiContacts();
      } else {
        await syncVtexContacts();
      }
      toast.success(t("contacts.empty.storeSyncQueued"));
      await load();
    } catch (e) {
      toast.error(
        integrationApiError(e, t("contacts.empty.storeSyncFail"))
      );
    } finally {
      setSyncingStore(false);
    }
  };

  // Espelha filtros principais na URL (compartilhar / deep-link).
  useEffect(() => {
    const next = new URLSearchParams();
    if (appliedSearch) {
      next.set("search", appliedSearch);
    }
    if (appliedPhone) {
      next.set("phone", appliedPhone);
    }
    if (statusFilter) {
      next.set("status", statusFilter);
    }
    if (trashedFilter === "only") {
      next.set("trashed", "only");
    }
    if (tagFilters.length > 0) {
      next.set("tag_ids", tagFilters.map((o) => String(o.value)).join(","));
    }
    if (hardBounceOnly) {
      next.set("hard_bounce_flagged", "1");
    }
    setSearchParams(next, { replace: true });
  }, [
    appliedSearch,
    appliedPhone,
    statusFilter,
    trashedFilter,
    tagFilters,
    hardBounceOnly,
    setSearchParams,
  ]);

  const resetForm = () => {
    setEmail("");
    setName("");
    setCountryCode("55");
    setPhoneNumber("");
    setCustomFields(valuesFromContactFields(sortedFieldDefs, {}));
    setSelectedTags([]);
    setFieldErrors({});
    setFormError(null);
    setEditing(null);
    setNewTagName("");
    setNewTagError(null);
  };

  const openCreate = () => {
    resetForm();
    setModalOpen(true);
    void probeTagsExist();
  };

  /** Snapshot dos filtros atuais — mesmo payload que a listagem (LAC-027). */
  const buildExportFilters = (): ContactExportFilters => {
    return {
      search: appliedSearch || undefined,
      phone: appliedPhone || undefined,
      status: statusFilter || undefined,
      trashed: trashedFilter === "only" ? "only" : undefined,
      tag_ids: tagFilters.length
        ? tagFilters.map((opt) => opt.value)
        : undefined,
      tag_match: tagFilters.length > 1 ? tagMatch : undefined,
      fields: fieldsQueryParam,
      hard_bounce_flagged: hardBounceOnly ? 1 : undefined,
    };
  };

  const openExport = () => {
    // Empty da lista filtrada — não enfileira Job inútil (DOC 13).
    if (!loading && total === 0) {
      toast.info(
        hasActiveFilters
          ? t("contacts.export.emptyFiltered")
          : t("contacts.export.emptyBase"),
        { autoClose: 4000 }
      );
      return;
    }
    setExportResumeId(null);
    setExportFilters(buildExportFilters());
    setExportOpen(true);
  };

  const openExportResume = (sessionId: number) => {
    setExportResumeId(sessionId);
    setExportFilters({});
    setExportOpen(true);
  };

  const reExportFromSession = (item: ExportSessionProgress) => {
    const f = item.filters || {};
    setExportResumeId(null);
    setExportFilters({
      search: f.search || undefined,
      phone: f.phone || undefined,
      status: f.status || undefined,
      trashed: f.trashed === "only" || f.trashed === "with" ? f.trashed : undefined,
      tag_ids: f.tag_ids?.length ? f.tag_ids : undefined,
      tag_match: f.tag_match === "and" || f.tag_match === "or" ? f.tag_match : undefined,
      fields: f.fields && Object.keys(f.fields).length > 0 ? f.fields : undefined,
    });
    setExportOpen(true);
  };

  const downloadFromHistory = async (item: ExportSessionProgress) => {
    if (!item.download_ready) {
      return;
    }
    setExportHistoryBusyId(item.id);
    try {
      const date =
        item.updated_at?.slice(0, 10) ||
        item.created_at?.slice(0, 10) ||
        new Date().toISOString().slice(0, 10);
      const rows = Math.max(0, item.total_rows ?? 0);
      await downloadContactsExportCsv(
        item.id,
        `contatos_${date}_${rows}linhas.csv`
      );
      toast.success(t("contacts.export.toast.downloaded"), { autoClose: 2500 });
    } catch (err: any) {
      toast.error(err?.message || t("contacts.export.error.download"), {
        autoClose: 4500,
      });
    } finally {
      setExportHistoryBusyId(null);
    }
  };

  const formatExportWhen = (iso?: string) => {
    if (!iso) {
      return "—";
    }
    try {
      return new Date(iso).toLocaleString(i18n.language || "pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  const openEdit = (contact: Contact) => {
    setEditing(contact);
    setEmail(contact.email);
    setName(contact.name || "");
    setCountryCode(contact.country_code || "55");
    setPhoneNumber(
      formatPhoneInput(
        contact.country_code || "55",
        contact.phone_number || ""
      )
    );
    setCustomFields(valuesFromContactFields(sortedFieldDefs, contact.fields));
    setSelectedTags(
      (contact.tags || []).map((tag) => ({ value: tag.id, label: tag.name }))
    );
    setFieldErrors({});
    setFormError(null);
    setNewTagName("");
    setNewTagError(null);
    setModalOpen(true);
    void probeTagsExist();
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const mapFieldErrors = (err: any): Record<string, string> => {
    const details = err?.details;
    if (details && typeof details === "object") {
      const mapped: Record<string, string> = {};
      Object.keys(details).forEach((key) => {
        mapped[key] = Array.isArray(details[key])
          ? details[key][0]
          : String(details[key]);
      });
      return mapped;
    }
    return {
      email: err?.message || t("contacts.error.save"),
    };
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    setError(null);

    const digitsPhone = digitsOnly(phoneNumber);
    const digitsCountry = digitsOnly(countryCode) || "55";
    const payload = {
      email: email.trim(),
      name: name.trim() || undefined,
      // null explícito limpa telefone no PATCH.
      country_code: digitsPhone ? digitsCountry : null,
      phone_number: digitsPhone || null,
      tag_ids: selectedTags.map((opt) => opt.value),
      fields: buildFieldsPayload(sortedFieldDefs, customFields),
    };

    try {
      if (editing) {
        const res = await updateContact(editing.id, payload);
        if (!res?.success) {
          setFieldErrors({ email: t("contacts.error.save") });
          return;
        }
        toast.success(res.message || t("contacts.toast.updated"), {
          autoClose: 2500,
        });
      } else {
        const res = await createContact(payload);
        if (!res?.success) {
          setFieldErrors({ email: t("contacts.error.save") });
          return;
        }
        toast.success(res.message || t("contacts.toast.created"), {
          autoClose: 2500,
        });
      }
      closeModal();
      await load();
    } catch (err: any) {
      // Domínio tipado: message leiga da API (nunca error.code) — DOC 13.
      if (
        err?.code === "FIELD_VALIDATION_ERROR" ||
        err?.code === "UNKNOWN_FIELD_SLUG" ||
        err?.code === "FIELD_NOT_FILTERABLE"
      ) {
        setFormError(err?.message || t("contacts.error.save"));
      } else {
        setFieldErrors(mapFieldErrors(err));
      }
    } finally {
      setSaving(false);
    }
  };

  /** Cria tag sem sair do modal do contato (DOC 13 § 3.7). */
  const submitNewTag = async () => {
    const trimmed = newTagName.trim();
    if (!trimmed) {
      setNewTagError(t("contacts.form.newTag.required"));
      return;
    }
    setCreatingTag(true);
    setNewTagError(null);
    try {
      const res = await createTag({ name: trimmed });
      if (!res?.success || !res.data) {
        setNewTagError(t("contacts.form.newTag.error"));
        return;
      }
      const created = res.data;
      setTagsExist(true);
      const option = { value: created.id, label: created.name };
      setSelectedTags((prev) =>
        prev.some((p) => p.value === option.value) ? prev : [...prev, option]
      );
      setNewTagName("");
      toast.success(res.message || t("contacts.form.newTag.success"), {
        autoClose: 2000,
      });
    } catch (err: any) {
      const details = err?.details?.name;
      setNewTagError(
        Array.isArray(details)
          ? details[0]
          : err?.message || t("contacts.form.newTag.error")
      );
    } finally {
      setCreatingTag(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeletingBusy(true);
    setError(null);
    try {
      const res = await deleteContact(deleting.id);
      toast.success(res?.message || t("contacts.toast.deleted"), {
        autoClose: 2500,
      });
      setDeleting(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("contacts.error.delete"));
      setDeleting(null);
    } finally {
      setDeletingBusy(false);
    }
  };

  const confirmRestore = async (contact: Contact) => {
    setRestoringId(contact.id);
    setError(null);
    try {
      const res = await restoreContact(contact.id);
      toast.success(res?.message || t("contacts.toast.restored"), {
        autoClose: 2500,
      });
      await load();
    } catch (err: any) {
      setError(err?.message || t("contacts.error.restore"));
    } finally {
      setRestoringId(null);
    }
  };

  const restoreHygieneBulk = async () => {
    if (
      !lastHygieneBulk ||
      lastHygieneBulk.count <= 0 ||
      lastHygieneBulk.contact_ids.length === 0
    ) {
      return;
    }
    setRestoringHygieneBulk(true);
    setError(null);
    try {
      const res = await bulkRestoreContacts({
        contact_ids: lastHygieneBulk.contact_ids,
      });
      if (!res?.success) {
        toast.error(t("contacts.hygieneBanner.restoreError"));
        return;
      }
      toast.success(
        t("contacts.hygieneBanner.restored", {
          count: res.data?.restored_count ?? lastHygieneBulk.count,
        })
      );
      setLastHygieneBulk(null);
      await load();
    } catch (err: any) {
      toast.error(err?.message || t("contacts.hygieneBanner.restoreError"));
    } finally {
      setRestoringHygieneBulk(false);
    }
  };

  const clearHardBounceAll = async () => {
    if (
      !window.confirm(t("contacts.bulk.clearHardBounceConfirm", { count: total }))
    ) {
      return;
    }
    try {
      const res = await clearHardBounceFlag({ all_flagged: 1 });
      toast.success(
        res?.message ||
          t("contacts.bulk.clearHardBounceDone", {
            count: res?.data?.cleared_count ?? total,
          }),
        { autoClose: 3000 }
      );
      await load();
    } catch (err: any) {
      toast.error(err?.message || t("contacts.bulk.clearHardBounceError"), {
        autoClose: 4000,
      });
    }
  };

  const releaseQuarantineAll = async () => {
    if (
      !window.confirm(
        t("contacts.bulk.releaseQuarantineConfirm", { count: total })
      )
    ) {
      return;
    }
    try {
      const res = await releaseQuarantineContacts({ all_quarantined: 1 });
      toast.success(
        res?.message ||
          t("contacts.bulk.releaseQuarantineDone", {
            count: res?.data?.released_count ?? total,
          }),
        { autoClose: 3000 }
      );
      await load();
    } catch (err: any) {
      toast.error(err?.message || t("contacts.bulk.releaseQuarantineError"), {
        autoClose: 4000,
      });
    }
  };

  const formatHygieneDate = (iso: string | null | undefined): string => {
    if (!iso) {
      return "—";
    }
    try {
      return new Intl.DateTimeFormat(i18n.language || "pt-BR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const toggleTrashedOne = (id: number) => {
    setSelectedTrashed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTrashedPage = () => {
    const pageIds = contacts.map((c) => c.id);
    const allSelected = pageIds.every((id) => selectedTrashed.has(id));
    setSelectedTrashed((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const confirmBulkRestore = async () => {
    if (selectedTrashed.size === 0) return;
    setBulkRestoring(true);
    setError(null);
    try {
      const res = await bulkRestoreContacts({
        contact_ids: Array.from(selectedTrashed),
      });
      toast.success(
        t("contacts.toast.bulkRestored", {
          count: res.data?.restored_count ?? selectedTrashed.size,
        }),
        { autoClose: 2500 }
      );
      setSelectedTrashed(new Set());
      await load();
    } catch (err: any) {
      setError(err?.message || t("contacts.error.restore"));
    } finally {
      setBulkRestoring(false);
    }
  };

  const confirmResendDoi = async (contact: Contact) => {
    setResendingDoiId(contact.id);
    setError(null);
    try {
      const res = await resendDoiContact(contact.id);
      toast.success(res?.message || t("contacts.toast.doiResent"), {
        autoClose: 2500,
      });
    } catch (err: any) {
      setError(err?.message || t("contacts.error.doiResend"));
    } finally {
      setResendingDoiId(null);
    }
  };

  const confirmReactivate = async () => {
    if (!reactivating) {
      return;
    }
    setReactivatingBusy(true);
    setError(null);
    try {
      const res = await reactivateContact(reactivating.id);
      toast.success(res?.message || t("contacts.toast.reactivated"), {
        autoClose: 2500,
      });
      setReactivating(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("contacts.error.reactivate"));
      setReactivating(null);
    } finally {
      setReactivatingBusy(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setPhone("");
    setAppliedPhone("");
    setTagFilters([]);
    setTagMatch("or");
    setStatusFilter("");
    setTrashedFilter("");
    setHardBounceOnly(false);
    setFieldFilters({});
    setPage(1);
  };

  const openSaveAsSegment = () => {
    if (!canSaveAsSegment) {
      toast.info(t("contacts.saveAsSegment.needRules"));
      return;
    }
    setSaveSegmentName("");
    setSaveSegmentError(null);
    setSaveSegmentOpen(true);
  };

  const submitSaveAsSegment = async () => {
    const name = saveSegmentName.trim();
    if (!name) {
      setSaveSegmentError(t("contacts.saveAsSegment.nameRequired"));
      return;
    }
    const rules = buildSegmentRulesFromContactFilters({
      status: statusFilter || undefined,
      tagIds: tagFilters.map((opt) => opt.value),
      tagMatch,
      fields: fieldsQueryParam,
    });
    if (!rules) {
      setSaveSegmentError(t("contacts.saveAsSegment.needRules"));
      return;
    }
    setSaveSegmentBusy(true);
    setSaveSegmentError(null);
    try {
      const res = await createSegment({ name, rules });
      if (!res?.success) {
        setSaveSegmentError(t("contacts.saveAsSegment.error"));
        return;
      }
      setSaveSegmentOpen(false);
      toast.success(
        <span>
          {t("contacts.saveAsSegment.success")}{" "}
          <Link to="/segments" className="fw-semibold text-decoration-underline">
            {t("contacts.saveAsSegment.openSegments")}
          </Link>
        </span>
      );
    } catch (e: any) {
      setSaveSegmentError(e?.message || t("contacts.saveAsSegment.error"));
    } finally {
      setSaveSegmentBusy(false);
    }
  };

  const clearPhoneFilter = () => {
    setPhone("");
    setAppliedPhone("");
    setPage(1);
  };

  const getFieldFilterEntry = (
    def: ContactFieldDefinition
  ): FieldFilterEntry => {
    const existing = fieldFilters[def.slug];
    if (existing) {
      return existing;
    }
    return { op: defaultOpForFieldType(def.type), value: "", valueTo: "" };
  };

  const patchFieldFilter = (
    slug: string,
    def: ContactFieldDefinition,
    patch: Partial<FieldFilterEntry>
  ) => {
    setFieldFilters((prev) => {
      const current = prev[slug] ?? {
        op: defaultOpForFieldType(def.type),
        value: "",
        valueTo: "",
      };
      const nextEntry = { ...current, ...patch };
      const next = { ...prev };
      const keepDraft =
        isFieldFilterActive(nextEntry) ||
        nextEntry.value.trim() !== "" ||
        (nextEntry.valueTo?.trim() ?? "") !== "";
      if (!keepDraft) {
        delete next[slug];
      } else {
        next[slug] = nextEntry;
      }
      return next;
    });
    setPage(1);
  };

  const clearFieldFilter = (slug: string) => {
    setFieldFilters((prev) => {
      const next = { ...prev };
      delete next[slug];
      return next;
    });
    setPage(1);
  };

  const formatFieldCell = (contact: Contact, def: ContactFieldDefinition) => {
    const raw = contact.fields?.[def.slug];
    if (raw === null || raw === undefined || raw === "") {
      return "—";
    }
    if (def.type === "boolean") {
      return raw === true || raw === "true" || raw === 1
        ? t("contacts.fields.booleanTrue")
        : t("contacts.fields.booleanFalse");
    }
    if (Array.isArray(raw)) {
      return raw.length ? raw.join(", ") : "—";
    }
    return String(raw);
  };

  const onTagFiltersChange = (opts: TagOption[]) => {
    setTagFilters(opts);
    setPage(1);
  };

  const statusBadge = (status: string, deletedAt?: string | null) => {
    const color =
      status === "active"
        ? "success"
        : status === "suppressed"
          ? "danger"
          : status === "unsubscribed"
            ? "warning"
            : status === "quarantine"
              ? "info"
              : status === "pending_doi"
                ? "warning"
                : "secondary";
    const labelKey = `contacts.status.${status}`;
    const label = t(labelKey, { defaultValue: status });
    return (
      <span className="d-inline-flex flex-wrap gap-1 align-items-center">
        <Badge color={color}>{label}</Badge>
        {deletedAt ? (
          <Badge color="dark" pill>
            {t("contacts.status.trashed")}
          </Badge>
        ) : null}
      </span>
    );
  };

  const emptyDescription = () => {
    if (
      tagFilters.length > 0 &&
      !appliedSearch &&
      !appliedPhone &&
      !statusFilter &&
      trashedFilter !== "only" &&
      activeFieldFilterEntries.length === 0
    ) {
      return t("contacts.empty.filteredTag.description");
    }
    if (appliedPhone && !appliedSearch && tagFilters.length === 0 && !statusFilter && trashedFilter !== "only" && activeFieldFilterEntries.length === 0) {
      return t("contacts.empty.filteredPhone.description");
    }
    if (hasActiveFilters) {
      return t("contacts.empty.filteredBoth.description");
    }
    return t("contacts.empty.descriptionWithImport");
  };

  const emptyTitle = () => {
    if (
      tagFilters.length > 0 &&
      !appliedSearch &&
      !appliedPhone &&
      !statusFilter &&
      trashedFilter !== "only" &&
      activeFieldFilterEntries.length === 0
    ) {
      return t("contacts.empty.filteredTag.title");
    }
    if (appliedPhone && !appliedSearch && tagFilters.length === 0 && !statusFilter && trashedFilter !== "only" && activeFieldFilterEntries.length === 0) {
      return t("contacts.empty.filteredPhone.title");
    }
    if (hasActiveFilters) {
      return t("contacts.empty.filteredSearch.title");
    }
    return t("contacts.empty.title");
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("contacts.title")}
          actions={
            <PageHelpLink
              href={helpUrlForRoute("/contacts", i18n.language || "pt-BR")}
              label={t("contacts.help")}
            />
          }
        />

        <ToastContainer closeButton={false} limit={1} />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex flex-wrap gap-2 flex-grow-1 align-items-center">
                  <div style={{ maxWidth: 280, flex: "1 1 200px" }}>
                    <Input
                      type="search"
                      placeholder={t("contacts.search.placeholder")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label={t("contacts.search.placeholder")}
                    />
                  </div>
                  <div style={{ maxWidth: 220, flex: "1 1 160px" }}>
                    <Input
                      type="search"
                      placeholder={t("contacts.filter.phone.placeholder")}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      aria-label={t("contacts.filter.phone")}
                    />
                  </div>
                  <div style={{ minWidth: 220, flex: "1 1 220px", maxWidth: 320 }}>
                    <AsyncSelect
                      classNamePrefix="select"
                      isMulti
                      isClearable
                      cacheOptions
                      defaultOptions
                      loadOptions={loadTagOptions}
                      value={tagFilters}
                      onChange={(opts: readonly TagOption[] | null) =>
                        onTagFiltersChange([...(opts || [])])
                      }
                      placeholder={t("contacts.filter.tagPlaceholder")}
                      noOptionsMessage={() => t("contacts.filter.noTags")}
                      aria-label={t("contacts.filter.tag")}
                    />
                  </div>
                  {tagFilters.length > 1 && (
                    <div className="d-flex gap-2 align-items-center">
                      <Label className="mb-0 small text-muted">
                        {t("contacts.filter.matchLabel")}
                      </Label>
                      <Input
                        type="select"
                        bsSize="sm"
                        style={{ width: "auto" }}
                        value={tagMatch}
                        onChange={(e) => {
                          setTagMatch(e.target.value as TagMatch);
                          setPage(1);
                        }}
                        aria-label={t("contacts.filter.matchLabel")}
                      >
                        <option value="or">{t("contacts.filter.match.or")}</option>
                        <option value="and">{t("contacts.filter.match.and")}</option>
                      </Input>
                    </div>
                  )}
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ width: "auto", minWidth: 150 }}
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    aria-label={t("contacts.filter.status")}
                  >
                    <option value="">{t("contacts.filter.statusAll")}</option>
                    <option value="active">{t("contacts.status.active")}</option>
                    <option value="pending_doi">
                      {t("contacts.status.pending_doi")}
                    </option>
                    <option value="unsubscribed">
                      {t("contacts.status.unsubscribed")}
                    </option>
                    <option value="quarantine">
                      {t("contacts.status.quarantine")}
                    </option>
                    <option value="suppressed">
                      {t("contacts.status.suppressed")}
                    </option>
                  </Input>
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ width: "auto", minWidth: 150 }}
                    value={trashedFilter}
                    onChange={(e) => {
                      setTrashedFilter(e.target.value as "" | "only");
                      setPage(1);
                    }}
                    aria-label={t("contacts.filter.trashed")}
                  >
                    <option value="">{t("contacts.filter.trashedActive")}</option>
                    <option value="only">{t("contacts.filter.trashedOnly")}</option>
                  </Input>
                  <div className="form-check mb-0 ms-1">
                    <Input
                      className="form-check-input"
                      id="contacts-hard-bounce-only"
                      type="checkbox"
                      checked={hardBounceOnly}
                      onChange={(e) => {
                        setHardBounceOnly(e.target.checked);
                        setPage(1);
                      }}
                    />
                    <Label
                      className="form-check-label small"
                      htmlFor="contacts-hard-bounce-only"
                    >
                      {t("contacts.filter.hardBounceOnly")}
                    </Label>
                  </div>
                  {hardBounceOnly && total > 0 ? (
                    <Button
                      color="soft-warning"
                      size="sm"
                      type="button"
                      onClick={() => void clearHardBounceAll()}
                    >
                      {t("contacts.bulk.clearHardBounce")}
                    </Button>
                  ) : null}
                  {statusFilter === "quarantine" && total > 0 ? (
                    <Button
                      color="soft-info"
                      size="sm"
                      type="button"
                      onClick={() => void releaseQuarantineAll()}
                    >
                      {t("contacts.bulk.releaseQuarantine")}
                    </Button>
                  ) : null}
                  {filterableDefs.map((def) => {
                    const entry = getFieldFilterEntry(def);
                    const ops = operatorsForFieldType(def.type);
                    const showOpSelect = ops.length > 1;

                    return (
                      <div
                        key={def.slug}
                        className="d-flex gap-1 align-items-center"
                        style={{
                          minWidth: showOpSelect ? 200 : 140,
                          flex: "1 1 200px",
                          maxWidth: 280,
                        }}
                      >
                        {showOpSelect && (
                          <Input
                            type="select"
                            bsSize="sm"
                            style={{ width: "auto", minWidth: 88, maxWidth: 110 }}
                            value={entry.op}
                            onChange={(e) =>
                              patchFieldFilter(def.slug, def, {
                                op: e.target.value as FieldFilterEntry["op"],
                              })
                            }
                            aria-label={t("contacts.filter.operator.label", {
                              label: def.label,
                            })}
                          >
                            {ops.map((op) => (
                              <option key={op} value={op}>
                                {operatorLabel(op, t)}
                              </option>
                            ))}
                          </Input>
                        )}
                        {def.type === "boolean" ? (
                          <Input
                            type="select"
                            bsSize="sm"
                            className="flex-grow-1"
                            value={entry.value}
                            onChange={(e) =>
                              patchFieldFilter(def.slug, def, {
                                value: e.target.value,
                              })
                            }
                            aria-label={def.label}
                          >
                            <option value="">
                              {t("contacts.filter.fieldAll", { label: def.label })}
                            </option>
                            <option value="true">
                              {def.label}: {t("contacts.fields.booleanTrue")}
                            </option>
                            <option value="false">
                              {def.label}: {t("contacts.fields.booleanFalse")}
                            </option>
                          </Input>
                        ) : def.type === "select" || def.type === "multiselect" ? (
                          entry.op === "in" ? (
                            <Select
                              classNamePrefix="select"
                              isMulti
                              className="flex-grow-1"
                              options={(def.options || []).map((opt) => ({
                                value: opt,
                                label: opt,
                              }))}
                              value={parseInValues(entry.value).map((v) => ({
                                value: v,
                                label: v,
                              }))}
                              onChange={(
                                opts: readonly { value: string; label: string }[] | null
                              ) =>
                                patchFieldFilter(def.slug, def, {
                                  value: (opts || [])
                                    .map((o) => o.value)
                                    .join(", "),
                                })
                              }
                              placeholder={def.label}
                              aria-label={def.label}
                            />
                          ) : (
                            <Input
                              type="select"
                              bsSize="sm"
                              className="flex-grow-1"
                              value={entry.value}
                              onChange={(e) =>
                                patchFieldFilter(def.slug, def, {
                                  value: e.target.value,
                                })
                              }
                              aria-label={def.label}
                            >
                              <option value="">
                                {t("contacts.filter.fieldAll", { label: def.label })}
                              </option>
                              {(def.options || []).map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </Input>
                          )
                        ) : entry.op === "between" ? (
                          <div className="d-flex gap-1 flex-grow-1">
                            <Input
                              type={
                                def.type === "date"
                                  ? "date"
                                  : def.type === "number"
                                    ? "number"
                                    : "text"
                              }
                              bsSize="sm"
                              placeholder={def.label}
                              value={entry.value}
                              onChange={(e) =>
                                patchFieldFilter(def.slug, def, {
                                  value: e.target.value,
                                })
                              }
                              aria-label={`${def.label} ${t("contacts.filter.operator.from")}`}
                            />
                            <Input
                              type={
                                def.type === "date"
                                  ? "date"
                                  : def.type === "number"
                                    ? "number"
                                    : "text"
                              }
                              bsSize="sm"
                              placeholder={t("contacts.filter.operator.to")}
                              value={entry.valueTo ?? ""}
                              onChange={(e) =>
                                patchFieldFilter(def.slug, def, {
                                  valueTo: e.target.value,
                                })
                              }
                              aria-label={`${def.label} ${t("contacts.filter.operator.to")}`}
                            />
                          </div>
                        ) : (
                          <Input
                            type={
                              def.type === "date"
                                ? "date"
                                : def.type === "number"
                                  ? "number"
                                  : "text"
                            }
                            bsSize="sm"
                            className="flex-grow-1"
                            placeholder={def.label}
                            value={entry.value}
                            onChange={(e) =>
                              patchFieldFilter(def.slug, def, {
                                value: e.target.value,
                              })
                            }
                            aria-label={def.label}
                          />
                        )}
                      </div>
                    );
                  })}
                  {hasActiveFilters && (
                    <Button
                      color="link"
                      className="text-decoration-underline p-0"
                      onClick={clearFilters}
                    >
                      {t("contacts.filter.clear")}
                    </Button>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {canSaveAsSegment && (
                    <Button
                      color="soft-info"
                      type="button"
                      onClick={openSaveAsSegment}
                      disabled={loading}
                    >
                      <i className="ri-filter-3-line me-1" aria-hidden="true" />
                      {t("contacts.saveAsSegment.cta")}
                    </Button>
                  )}
                  <Button
                    color="soft-success"
                    type="button"
                    onClick={openExport}
                    disabled={loading}
                  >
                    <i className="ri-download-2-line me-1" aria-hidden="true" />
                    {t("contacts.export.cta")}
                  </Button>
                  <Button color="soft-primary" tag={Link} to="/contacts/import">
                    {t("contacts.import.cta")}
                  </Button>
                  <Button
                    color="soft-secondary"
                    tag={Link}
                    to="/settings/contact-fields"
                  >
                    {t("menu.contactFields")}
                  </Button>
                  <Button color="soft-secondary" tag={Link} to="/contacts/blocklist">
                    {t("menu.blocklist")}
                  </Button>
                  <Button color="primary" onClick={openCreate}>
                    {t("contacts.create")}
                  </Button>
                </div>
              </CardHeader>
              {(appliedPhone || activeFieldFilterEntries.length > 0) && (
                <div className="px-3 pt-3 d-flex flex-wrap gap-1">
                  {appliedPhone ? (
                    <Badge
                      color="primary"
                      pill
                      className="d-inline-flex align-items-center gap-1"
                    >
                      {t("contacts.filter.phone.chip", { phone: appliedPhone })}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-white lh-1"
                        aria-label={t("contacts.filter.phone.remove")}
                        onClick={clearPhoneFilter}
                      >
                        <i className="ri-close-line" />
                      </button>
                    </Badge>
                  ) : null}
                  {activeFieldFilterEntries.map(([slug, entry]) => {
                    const def = filterableDefs.find((d) => d.slug === slug);
                    const label = def?.label || slug;
                    const display = def
                      ? formatFieldFilterChip(def, entry, t)
                      : `${label}: ${entry.value}`;
                    return (
                      <Badge
                        key={slug}
                        color="primary"
                        pill
                        className="d-inline-flex align-items-center gap-1"
                      >
                        {display}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-white lh-1"
                          aria-label={t("contacts.filter.removeField", {
                            label,
                          })}
                          onClick={() => clearFieldFilter(slug)}
                        >
                          <i className="ri-close-line" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
              {hasNoTags && (
                <div className="px-3 pt-3">
                  <Alert color="info" className="mb-0 py-2">
                    {t("contacts.filter.noTagsHint")}{" "}
                    <Link to="/tags" className="fw-medium">
                      {t("contacts.filter.goToTags")}
                    </Link>
                  </Alert>
                </div>
              )}
              {hasNoCustomFields && (
                <div className="px-3 pt-3">
                  <Alert color="secondary" className="mb-0 py-2">
                    {t("contacts.fields.emptyCatalog")}{" "}
                    <Link to="/settings/contact-fields" className="fw-medium">
                      {t("contacts.fields.goToCatalog")}
                    </Link>
                  </Alert>
                </div>
              )}
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-center py-5">
                    {!hasActiveFilters && (
                      <div className="avatar-lg mx-auto mb-3">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-1">
                          <i className="ri-contacts-book-2-line" />
                        </div>
                      </div>
                    )}
                    <h5 className="mb-2">{emptyTitle()}</h5>
                    <p className="text-muted mb-3">{emptyDescription()}</p>
                    {hasActiveFilters ? (
                      <Button color="light" onClick={clearFilters}>
                        {t("contacts.filter.clear")}
                      </Button>
                    ) : (
                      <div className="d-flex flex-column align-items-center gap-2">
                        <div className="d-flex flex-wrap gap-2 justify-content-center">
                          <Button
                            color="primary"
                            tag={Link}
                            to="/contacts/import"
                          >
                            {t("contacts.empty.importCta")}
                          </Button>
                          <Button color="light" onClick={openCreate}>
                            {t("contacts.empty.cta")}
                          </Button>
                          <Button color="light" tag={Link} to="/forms">
                            {t("contacts.empty.formsCta")}
                          </Button>
                          <Button
                            color="light"
                            tag={Link}
                            to="/settings/contact-fields"
                          >
                            {t("menu.contactFields")}
                          </Button>
                          {hasNoTags && (
                            <Button color="light" tag={Link} to="/tags">
                              {t("contacts.filter.goToTags")}
                            </Button>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-2 justify-content-center small">
                          <span className="text-muted align-self-center">
                            {t("contacts.empty.packsHint")}
                          </span>
                          <Link to="/settings/contact-fields?pack=educacao">
                            {t("platformTemplates.tag.educacao")}
                          </Link>
                          <Link to="/settings/contact-fields?pack=saas">
                            {t("platformTemplates.tag.saas")}
                          </Link>
                          <Link to="/settings/contact-fields?pack=publisher">
                            {t("platformTemplates.tag.publisher")}
                          </Link>
                        </div>
                        {/* Loja é opcional — caminho “Starter sem loja” vem primeiro. */}
                        <div className="d-flex flex-wrap gap-2 justify-content-center">
                          {storeSlug ? (
                            <Button
                              color="link"
                              className="text-muted"
                              disabled={syncingStore}
                              onClick={() => void onSyncStoreFromEmpty()}
                            >
                              {syncingStore && (
                                <Spinner size="sm" className="me-1" />
                              )}
                              {t("contacts.empty.storeSyncCta", {
                                store: storeSlug.toUpperCase(),
                              })}
                            </Button>
                          ) : (
                            <Button
                              color="link"
                              className="text-muted"
                              tag={Link}
                              to="/integrations"
                            >
                              {t("contacts.empty.storeConnectCta")}
                            </Button>
                          )}
                        </div>
                        <a
                          href={helpArticleUrl(
                            "/getting-started/starter-sem-loja",
                            i18n.language
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-link"
                        >
                          {t("contacts.empty.helpStarter")}
                          <i className="ri-external-link-line ms-1 align-middle" />
                        </a>
                        <a
                          href={helpUrlForRoute(
                            "/contacts",
                            i18n.language
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-link btn-sm text-muted"
                        >
                          {t("contacts.empty.help")}
                          <i className="ri-external-link-line ms-1 align-middle" />
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {trashedFilter === "only" &&
                      lastHygieneBulk &&
                      lastHygieneBulk.count > 0 && (
                        <Alert color="info" className="mb-3 py-2">
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <span>
                              {t("contacts.hygieneBanner.message", {
                                count: lastHygieneBulk.count,
                                date: formatHygieneDate(lastHygieneBulk.at),
                              })}
                            </span>
                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                color="soft-success"
                                size="sm"
                                disabled={restoringHygieneBulk || loading}
                                onClick={() => void restoreHygieneBulk()}
                              >
                                {restoringHygieneBulk ? (
                                  <Spinner size="sm" />
                                ) : (
                                  t("contacts.hygieneBanner.restore")
                                )}
                              </Button>
                              <Link
                                to="/contacts/inactive"
                                className="btn btn-sm btn-soft-secondary"
                              >
                                {t("contacts.hygieneBanner.inactiveLink")}
                              </Link>
                            </div>
                          </div>
                        </Alert>
                      )}
                    <p className="text-muted mb-3">
                      {t("contacts.totalCount", { count: total })}
                    </p>
                    {trashedFilter === "only" && contacts.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        <Button
                          color="soft-success"
                          size="sm"
                          disabled={
                            selectedTrashed.size === 0 || bulkRestoring || loading
                          }
                          onClick={() => void confirmBulkRestore()}
                        >
                          {bulkRestoring ? (
                            <Spinner size="sm" />
                          ) : (
                            t("contacts.restore.bulkButton", {
                              count: selectedTrashed.size,
                            })
                          )}
                        </Button>
                      </div>
                    )}
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            {trashedFilter === "only" && (
                              <th style={{ width: 40 }}>
                                <Input
                                  type="checkbox"
                                  checked={
                                    contacts.length > 0 &&
                                    contacts.every((c) =>
                                      selectedTrashed.has(c.id)
                                    )
                                  }
                                  onChange={toggleTrashedPage}
                                  aria-label={t("contacts.restore.selectPage")}
                                />
                              </th>
                            )}
                            <th>{t("contacts.table.email")}</th>
                            <th>{t("contacts.table.name")}</th>
                            <th className="d-none d-md-table-cell">
                              {t("contacts.table.phone")}
                            </th>
                            {tableFieldDefs.map((def) => (
                              <th key={def.slug}>{def.label}</th>
                            ))}
                            <th>{t("contacts.table.status")}</th>
                            <th>{t("contacts.table.tags")}</th>
                            <th style={{ width: 220 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {contacts.map((c) => (
                            <tr key={c.id}>
                              {trashedFilter === "only" && (
                                <td>
                                  <Input
                                    type="checkbox"
                                    checked={selectedTrashed.has(c.id)}
                                    onChange={() => toggleTrashedOne(c.id)}
                                    aria-label={t("contacts.restore.selectOne", {
                                      email: c.email,
                                    })}
                                  />
                                </td>
                              )}
                              <td>{c.email}</td>
                              <td>{c.name || "—"}</td>
                              <td className="small text-muted d-none d-md-table-cell">
                                {formatPhoneListCell(
                                  c.country_code,
                                  c.phone_number
                                )}
                              </td>
                              {tableFieldDefs.map((def) => (
                                <td key={def.slug}>{formatFieldCell(c, def)}</td>
                              ))}
                              <td>
                                {statusBadge(c.status, c.deleted_at)}
                                {c.hard_bounce_flagged ? (
                                  <Badge
                                    color="danger"
                                    className="ms-1"
                                    title={t("contacts.hardBounceFlaggedHint")}
                                  >
                                    {t("contacts.hardBounceFlagged")}
                                  </Badge>
                                ) : null}
                              </td>
                              <td>
                                {(c.tags || []).length === 0
                                  ? "—"
                                  : c.tags.map((tag) => (
                                      <Badge
                                        key={tag.id}
                                        color="info"
                                        className="me-1"
                                      >
                                        {tag.name}
                                      </Badge>
                                    ))}
                              </td>
                              <td className="text-end">
                                {!c.deleted_at ? (
                                  <>
                                    {c.status === "unsubscribed" ? (
                                      <Button
                                        color="soft-success"
                                        size="sm"
                                        className="me-1"
                                        onClick={() => setReactivating(c)}
                                      >
                                        {t("contacts.reactivate.button")}
                                      </Button>
                                    ) : null}
                                    {c.status === "pending_doi" ? (
                                      <Button
                                        color="soft-warning"
                                        size="sm"
                                        className="me-1"
                                        disabled={resendingDoiId === c.id}
                                        onClick={() => confirmResendDoi(c)}
                                      >
                                        {resendingDoiId === c.id ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          t("contacts.doiResend.button")
                                        )}
                                      </Button>
                                    ) : null}
                                    <Button
                                      color="soft-primary"
                                      size="sm"
                                      className="me-1"
                                      onClick={() => openEdit(c)}
                                    >
                                      {t("contacts.edit.button")}
                                    </Button>
                                    <Button
                                      color="soft-danger"
                                      size="sm"
                                      onClick={() => setDeleting(c)}
                                    >
                                      {t("contacts.delete.button")}
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    color="soft-success"
                                    size="sm"
                                    disabled={restoringId === c.id}
                                    onClick={() => confirmRestore(c)}
                                  >
                                    {restoringId === c.id ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      t("contacts.restore.button")
                                    )}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {lastPage > 1 && (
                      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
                        <span className="text-muted small">
                          {t("contacts.pagination.label", {
                            page,
                            lastPage,
                            total,
                          })}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            {t("contacts.pagination.prev")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page >= lastPage || loading}
                            onClick={() =>
                              setPage((p) => Math.min(lastPage, p + 1))
                            }
                          >
                            {t("contacts.pagination.next")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>

            {exportRecent.length > 0 && (
              <Card className="mt-3">
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("contacts.export.history.title")}
                  </h5>
                </CardHeader>
                <CardBody className="pt-0">
                  <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("contacts.export.history.when")}</th>
                          <th>{t("contacts.export.history.status")}</th>
                          <th>{t("contacts.export.history.rows")}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {exportRecent.map((item) => (
                          <tr key={item.id}>
                            <td className="small text-muted">
                              {formatExportWhen(item.created_at)}
                            </td>
                            <td>
                              <Badge
                                color={
                                  item.status === "completed"
                                    ? "success"
                                    : item.status === "failed"
                                      ? "danger"
                                      : item.status === "processing"
                                        ? "primary"
                                        : "secondary"
                                }
                              >
                                {t(`contacts.export.status.${item.status}`, {
                                  defaultValue: item.status,
                                })}
                              </Badge>
                            </td>
                            <td className="small">
                              {item.processed_rows}/{item.total_rows || "—"}
                            </td>
                            <td className="text-end">
                              <div className="d-flex flex-wrap gap-1 justify-content-end">
                                {item.download_ready && (
                                  <Button
                                    color="soft-success"
                                    size="sm"
                                    type="button"
                                    disabled={exportHistoryBusyId === item.id}
                                    onClick={() => void downloadFromHistory(item)}
                                  >
                                    {exportHistoryBusyId === item.id ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      t("contacts.export.history.download")
                                    )}
                                  </Button>
                                )}
                                {(item.status === "pending" ||
                                  item.status === "processing") && (
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    type="button"
                                    onClick={() => openExportResume(item.id)}
                                  >
                                    {t("contacts.export.history.open")}
                                  </Button>
                                )}
                                {(item.status === "completed" ||
                                  item.status === "failed") && (
                                  <Button
                                    color="soft-secondary"
                                    size="sm"
                                    type="button"
                                    onClick={() => reExportFromSession(item)}
                                  >
                                    {t("contacts.export.history.again")}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modalOpen} toggle={closeModal} centered size="lg">
        <Form onSubmit={submitForm}>
          <ModalHeader toggle={closeModal}>
            {editing ? t("contacts.edit.title") : t("contacts.create")}
          </ModalHeader>
          <ModalBody>
            {formError && (
              <Alert color="danger" className="py-2">
                {formError}
              </Alert>
            )}
            {editing?.hard_bounce_flagged ? (
              <Alert color="warning" className="py-2">
                <Badge color="danger" className="me-2">
                  {t("contacts.hardBounceFlagged")}
                </Badge>
                {t("contacts.hardBounceFlaggedHint")}
              </Alert>
            ) : null}
            <div className="mb-3">
              <Label htmlFor="contact-email">{t("contacts.form.email")}</Label>
              <Input
                id="contact-email"
                type="email"
                value={email}
                invalid={!!fieldErrors.email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {fieldErrors.email && (
                <FormFeedback>{fieldErrors.email}</FormFeedback>
              )}
            </div>
            <div className="mb-3">
              <Label htmlFor="contact-name">{t("contacts.form.name")}</Label>
              <Input
                id="contact-name"
                type="text"
                value={name}
                invalid={!!fieldErrors.name}
                onChange={(e) => setName(e.target.value)}
              />
              {fieldErrors.name && (
                <FormFeedback>{fieldErrors.name}</FormFeedback>
              )}
            </div>
            <div className="mb-3">
              <Label htmlFor="contact-phone">{t("contacts.form.phone")}</Label>
              <div className="d-flex gap-2">
                <Input
                  id="contact-country-code"
                  type="text"
                  inputMode="numeric"
                  className="flex-shrink-0"
                  style={{ maxWidth: 88 }}
                  value={countryCode}
                  invalid={!!fieldErrors.country_code}
                  onChange={(e) => {
                    const nextCc = digitsOnly(e.target.value).slice(0, 4);
                    setCountryCode(nextCc);
                    // Remáscara o número se mudou DDI BR ↔ outro.
                    setPhoneNumber((prev) => formatPhoneInput(nextCc || "55", prev));
                  }}
                  aria-label={t("contacts.form.countryCode")}
                />
                <Input
                  id="contact-phone"
                  type="text"
                  inputMode="tel"
                  className="flex-grow-1"
                  value={phoneNumber}
                  invalid={!!fieldErrors.phone_number || !!fieldErrors.phone}
                  onChange={(e) =>
                    setPhoneNumber(
                      formatPhoneInput(countryCode || "55", e.target.value)
                    )
                  }
                  placeholder={t("contacts.form.phonePlaceholder")}
                />
              </div>
              <div className="form-text">{t("contacts.form.phoneHelp")}</div>
              {(fieldErrors.phone_number ||
                fieldErrors.phone ||
                fieldErrors.country_code) && (
                <div className="invalid-feedback d-block">
                  {fieldErrors.phone_number ||
                    fieldErrors.phone ||
                    fieldErrors.country_code}
                </div>
              )}
            </div>
            <ContactTypedFieldInputs
              definitions={sortedFieldDefs}
              values={customFields}
              errors={fieldErrors}
              disabled={saving}
              showReplaceHint={!!editing}
              onChange={(slug, value) =>
                setCustomFields((prev) => ({ ...prev, [slug]: value }))
              }
            />
            {hasNoCustomFields && (
              <Alert color="light" className="border mb-3 py-2">
                {t("contacts.fields.emptyCatalog")}{" "}
                <Link to="/settings/contact-fields" onClick={closeModal}>
                  {t("contacts.fields.goToCatalog")}
                </Link>
              </Alert>
            )}
            <div className="mb-3">
              <Label htmlFor="contact-tags">{t("contacts.form.tags")}</Label>
              <AsyncSelect
                inputId="contact-tags"
                classNamePrefix="select"
                isMulti
                cacheOptions
                defaultOptions
                loadOptions={loadTagOptions}
                value={selectedTags}
                onChange={(opts: readonly TagOption[] | null) =>
                  setSelectedTags([...(opts || [])])
                }
                placeholder={t("contacts.form.tagsPlaceholder")}
                noOptionsMessage={() => t("contacts.filter.noTags")}
              />
              {fieldErrors.tag_ids && (
                <div className="invalid-feedback d-block">
                  {fieldErrors.tag_ids}
                </div>
              )}
              <small className="text-muted d-block mt-1">
                {t("contacts.form.tagsHelp")}
              </small>
            </div>
            <div className="mb-0">
              <Label htmlFor="contact-new-tag">
                {t("contacts.form.newTag.label")}
              </Label>
              <div className="d-flex gap-2">
                <Input
                  id="contact-new-tag"
                  type="text"
                  value={newTagName}
                  invalid={!!newTagError}
                  onChange={(e) => {
                    setNewTagName(e.target.value);
                    setNewTagError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void submitNewTag();
                    }
                  }}
                  placeholder={t("contacts.form.newTag.placeholder")}
                  disabled={creatingTag}
                />
                <Button
                  type="button"
                  color="light"
                  onClick={() => void submitNewTag()}
                  disabled={creatingTag}
                >
                  {creatingTag ? (
                    <Spinner size="sm" />
                  ) : (
                    t("contacts.form.newTag.submit")
                  )}
                </Button>
              </div>
              {newTagError && (
                <div className="invalid-feedback d-block">{newTagError}</div>
              )}
              {hasNoTags && (
                <small className="text-muted d-block mt-1">
                  {t("contacts.form.newTag.emptyHint")}{" "}
                  <Link to="/tags" onClick={closeModal}>
                    {t("contacts.filter.goToTags")}
                  </Link>
                </small>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeModal}>
              {t("contacts.form.cancel")}
            </Button>
            <Button type="submit" color="primary" disabled={saving}>
              {saving ? <Spinner size="sm" /> : t("contacts.form.save")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Confirmação destrutiva com nome do registro — DOC 13 § 3.4 */}
      <Modal
        isOpen={!!deleting}
        toggle={() => !deletingBusy && setDeleting(null)}
        centered
      >
        <ModalHeader toggle={() => !deletingBusy && setDeleting(null)}>
          {t("contacts.delete.title")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            {t("contacts.delete.confirm", { email: deleting?.email || "" })}
          </p>
          <p className="text-muted small mb-0 mt-2">
            {t("contacts.delete.consequence")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={deletingBusy}
            onClick={() => setDeleting(null)}
          >
            {t("contacts.form.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={deletingBusy}
            onClick={() => void confirmDelete()}
          >
            {deletingBusy ? (
              <Spinner size="sm" />
            ) : (
              t("contacts.delete.confirmButton")
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={saveSegmentOpen}
        toggle={() => !saveSegmentBusy && setSaveSegmentOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !saveSegmentBusy && setSaveSegmentOpen(false)}>
          {t("contacts.saveAsSegment.title")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            {t("contacts.saveAsSegment.help")}
          </p>
          {(appliedSearch || appliedPhone || trashedFilter === "only") && (
            <Alert color="warning" className="py-2">
              {t("contacts.saveAsSegment.partialHint")}
            </Alert>
          )}
          <Label htmlFor="save-segment-name">
            {t("contacts.saveAsSegment.name")}
          </Label>
          <Input
            id="save-segment-name"
            value={saveSegmentName}
            disabled={saveSegmentBusy}
            maxLength={120}
            onChange={(e) => setSaveSegmentName(e.target.value)}
          />
          {saveSegmentError && (
            <FormFeedback className="d-block">{saveSegmentError}</FormFeedback>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={saveSegmentBusy}
            onClick={() => setSaveSegmentOpen(false)}
          >
            {t("contacts.form.cancel")}
          </Button>
          <Button
            color="primary"
            disabled={saveSegmentBusy}
            onClick={() => void submitSaveAsSegment()}
          >
            {saveSegmentBusy ? (
              <Spinner size="sm" />
            ) : (
              t("contacts.saveAsSegment.submit")
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reativar unsubscribed: Carla confirma pedido verbal do destinatário. */}
      <Modal
        isOpen={!!reactivating}
        toggle={() => !reactivatingBusy && setReactivating(null)}
        centered
      >
        <ModalHeader toggle={() => !reactivatingBusy && setReactivating(null)}>
          {t("contacts.reactivate.title")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            {t("contacts.reactivate.confirm", {
              email: reactivating?.email || "",
            })}
          </p>
          <p className="text-muted small mb-0 mt-2">
            {t("contacts.reactivate.consequence")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={reactivatingBusy}
            onClick={() => setReactivating(null)}
          >
            {t("contacts.form.cancel")}
          </Button>
          <Button
            color="success"
            disabled={reactivatingBusy}
            onClick={() => void confirmReactivate()}
          >
            {reactivatingBusy ? (
              <Spinner size="sm" />
            ) : (
              t("contacts.reactivate.confirmButton")
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {exportOpen && (
        <ContactExportModal
          isOpen={exportOpen}
          filters={exportFilters}
          resumeSessionId={exportResumeId}
          onClose={() => {
            setExportOpen(false);
            setExportResumeId(null);
          }}
          onSettled={() => void loadExportRecent()}
        />
      )}
    </div>
  );
};

export default ContactsList;
